import React, { useState } from 'react';
import {
  MdArrowBack, MdScience, MdSend, MdExpandMore, MdExpandLess,
  MdCheckCircle, MdOutlineConstruction,
} from 'react-icons/md';
import { useProfile } from '../contexts/ProfileContext';
import justinGroundTruth from '../personas/justin_lee_ground_truth.json';

// Dev-only screen: shows the injected ground-truth scenarios for the loaded
// persona so IVA's answers can be checked against known facts. One entry per
// persona that ships a ground-truth file; personas without one never surface
// the entry-point button (see hasGroundTruth in App.js).
const GROUND_TRUTH = {
  'justin-lee': justinGroundTruth,
};

export const hasGroundTruth = (source) => !!GROUND_TRUTH[source];

// Chip colour per target agent — matches the loose tint language used by the
// other screens (td vars where the palette has one, fixed hues otherwise).
const AGENT_TINTS = {
  subscription: '#946E9F',
  budgetPulse:  '#448889',
  anomaly:      '#D32F2F',
  goalTracker:  '#2FBF00',
  literacyHook: '#7C5636',
  profiler:     '#1565C0',
  healthScore:  '#00695C',
  spendDigest:  '#E65100',
};

const TestScenariosScreen = ({ onNavigate, onBack, onChatHandoff }) => {
  const { loadedProfileData } = useProfile();
  const truth = GROUND_TRUTH[loadedProfileData?.source];
  const [expandedId, setExpandedId] = useState(null);

  return (
    <div className="screen bh-flow">
      <header className="bh-flow-hero">
        <div className="bh-flow-hero-left">
          <button className="bh-flow-back" onClick={onBack || (() => onNavigate('home'))} aria-label="Back">
            <MdArrowBack />
          </button>
          <h1 className="bh-flow-title">Test Scenarios</h1>
        </div>
        <MdScience size={22} style={{ color: 'var(--bh-black)' }} />
      </header>

      <div className="bh-flow-body">
        {!truth ? (
          <div className="bh-info-box">
            No ground-truth file for the loaded profile. Load a persona with
            injected test scenarios (Justin Lee) to use this screen.
          </div>
        ) : (
          <>
            <div className="bh-form-card" style={{ gap: 'var(--space-2xs)' }}>
              <span style={{ font: 'var(--bh-overline)', textTransform: 'uppercase', color: 'var(--bh-gray)' }}>
                Ground truth · {truth.personaFile}
              </span>
              <span style={{ font: 'var(--bh-body-sm)', color: 'var(--bh-gray)' }}>
                Data window {truth.dataWindow}. Chequing ${truth.balances.chequing.toLocaleString('en-CA', { minimumFractionDigits: 2 })} ·
                Savings ${truth.balances.savings.toLocaleString('en-CA', { minimumFractionDigits: 2 })} ·
                Visa owing ${truth.balances.visaOwing.toLocaleString('en-CA', { minimumFractionDigits: 2 })} of ${truth.balances.visaLimit.toLocaleString('en-CA')} at {truth.balances.visaRate}.
              </span>
              <span style={{ font: 'var(--bh-caption)', color: 'var(--bh-gray)' }}>
                Tap a question to send it to IVA, then compare the reply against the expected answer.
              </span>
            </div>

            <section>
              <h2 className="bh-section-title" style={{ paddingLeft: 0 }}>
                {truth.scenarios.length} planted scenarios
              </h2>
              <div className="bh-recent-list">
                {truth.scenarios.map(s => {
                  const isOpen = expandedId === s.id;
                  const tint = AGENT_TINTS[s.agent] || 'var(--bh-gray)';
                  const built = /^built/.test(s.agentStatus);
                  return (
                    <div
                      key={s.id}
                      className="bh-recent-row"
                      style={{ flexDirection: 'column', alignItems: 'stretch', cursor: 'pointer' }}
                      onClick={() => setExpandedId(isOpen ? null : s.id)}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                        <span style={{
                          width: 36, height: 36, background: tint, color: 'var(--bh-white)',
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          flexShrink: 0, borderRadius: 'var(--radius-md)',
                        }}>
                          {built ? <MdCheckCircle size={18} /> : <MdOutlineConstruction size={18} />}
                        </span>
                        <div className="bh-recent-row-info" style={{ flex: 1, minWidth: 0 }}>
                          <span className="bh-recent-row-name">{s.title}</span>
                          <span className="bh-recent-row-date" style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            <span style={{
                              background: tint, color: 'var(--bh-white)',
                              padding: '2px 7px', font: 'var(--bh-caption)', fontWeight: 700,
                              textTransform: 'uppercase', letterSpacing: '0.04em',
                              borderRadius: 'var(--radius-full)',
                            }}>
                              {s.agent}
                            </span>
                            {s.agentStatus}
                          </span>
                        </div>
                        {isOpen ? <MdExpandLess size={20} style={{ color: 'var(--bh-gray)' }} /> : <MdExpandMore size={20} style={{ color: 'var(--bh-gray)' }} />}
                      </div>

                      {isOpen && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2xs)', marginTop: 'var(--space-2xs)' }}>
                          {s.questions.map(q => (
                            <button
                              key={q}
                              className="bh-btn-outline-dark"
                              style={{ display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left', justifyContent: 'space-between' }}
                              onClick={(e) => { e.stopPropagation(); onChatHandoff?.(q); }}
                            >
                              <span style={{ flex: 1, minWidth: 0 }}>{q}</span>
                              <MdSend size={14} style={{ flexShrink: 0 }} />
                            </button>
                          ))}
                          <div style={{ font: 'var(--bh-overline)', textTransform: 'uppercase', color: 'var(--bh-gray)', marginTop: 'var(--space-2xs)' }}>
                            Expected answer
                          </div>
                          <div style={{ font: 'var(--bh-body-sm)', color: 'var(--bh-black)' }}>
                            {s.expectedAnswer}
                          </div>
                          <div style={{ font: 'var(--bh-overline)', textTransform: 'uppercase', color: 'var(--bh-gray)', marginTop: 'var(--space-2xs)' }}>
                            Facts
                          </div>
                          <pre style={{
                            font: 'var(--bh-caption)', color: 'var(--bh-gray)', margin: 0,
                            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                          }}>
                            {JSON.stringify(s.facts, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>

            <div className="bh-info-box">
              Dev tool — scenarios are planted by {truth.generator}. This file
              doubles as the question bank for the automated prompt test harness.
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TestScenariosScreen;
